import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface User {
    id: string
    username?: string
    usernameType?: string
    phone: string
    whatsapp: string
    email: string
    firstName: string
    lastName: string
    profileUrl: string
}

export interface Member {
    id: string
    userId: string
    membershipTypeId: string
    designation: string
    firstName: string
    secondName: string
    lastName: string
    phone: string
    email: string
    profileUrl: string
    role: string
    isManager: boolean
    isMember: boolean
    isRegistrationComplete: boolean
    hasMembershipRemainder: boolean
    hasAutoBilling: boolean
    membershipNumber: string
    membershipCustomNumber: string
    membershipApproved: boolean
    membershipStartDate: string
    membershipNextBillingDate: string
    isApproved: boolean
    hasExpire: boolean
    isExpired: boolean
    startDate: string
    endDate: string
    status: string
    createdAt: string
    updatedAt: string
}

export interface LoginData {
    username: string,
    usernameType: string, // default to email
    password?: string,
    rememberMe?: boolean
}

export interface RegisterForm {
    username?: string;
    firstName: string;
    lastName: string;
    phone: string;
    subdomain: string;
    organization: string;
    email: string;
}

export const useAuthStore = defineStore('auth', () => {
    const isLoading = ref(false)
    const error = ref<string | null>(null)
    const user = ref<User | null>(null)
    const member = ref<Member | null>(null)
    const refreshToken = ref<string | boolean | null>(localStorage.getItem('refreshToken'))
    const accessToken = ref<string | boolean | null>(localStorage.getItem('accessToken'))
    const isAuthenticated = computed(() => !!accessToken.value && !!refreshToken.value)
    const username = computed(() => {
        if (user.value) {
            var names = [];
            if (user.value.firstName) names.push(user.value.firstName);
            if (user.value.lastName) names.push(user.value.lastName);
            if (names.length) {
                return names.join(' ');
            } else if (user.value.username) {
                return user.value.username;
            }
        }

        return '';
    })

    const profileUrl = computed(() => {
        if (user.value && user.value.profileUrl) {
            return user.value.profileUrl;
        }
        return '/core/images/user-avatar.png';
    })
    // const prefixUrl = (window.$getMainDomain(window.location.origin) == window.location.host)? '/register':'/auth';

    const sendOTP = async (form: LoginData, url?: string): Promise<boolean> => {
        isLoading.value = true
        error.value = null

        try {
            const query = `mutation Login($input:OtpInput!) {
                otp(input:$input) {status,message}
            }`;
            const variables = { input: { ...form } }
            const data = await window.$ajaxGraphql(query, variables, url || `/graphql/auth`)

            if (data.errors) {
                error.value = data.errors[0]?.message || 'Login failed'
            } else if (data.otp && data.otp.status) {
                return true;
            }
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Login failed'
        } finally {
            isLoading.value = false
        }

        return false
    }

    const login = async (form: LoginData, loadUser?: boolean, url?: string) => {
        isLoading.value = true
        error.value = null

        try {
            const query = `mutation Login($input:LoginInput!) {
                login(input:$input) {accessToken,refreshToken}
            }`;
            const variables = { input: { ...form, type: "OTP" } }
            const data = await window.$ajaxGraphql(query, variables, url || `/graphql/auth`)

            if (data.errors) {
                error.value = data.errors[0]?.message || 'Login failed'
            } else if (data.login) {
                user.value = data.login
                accessToken.value = true
                refreshToken.value = true

                localStorage.setItem('accessToken', JSON.stringify(accessToken.value))
                localStorage.setItem('refreshToken', JSON.stringify(refreshToken.value))

                if (loadUser) {
                    await getAuthorizedUser()
                }
            }

            return data
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Login failed'
        } finally {
            isLoading.value = false
        }

        return null
    }

    const loginWithGoogle = async (token: string, url?: string) => {
        isLoading.value = true
        error.value = null

        try {
            const query = `mutation Login($input:LoginInput!) {
                login(input:$input) {accessToken,refreshToken}
            }`;
            const variables = { input: { accessToken: token, type: "GOOGLE", username: "", usernameType: "email" } }
            const data = await window.$ajaxGraphql(query, variables, url || `/graphql/auth`)

            if (data.errors) {
                error.value = data.errors[0]?.message || 'Google Login failed'
            } else if (data.login) {
                user.value = data.login
                accessToken.value = true
                refreshToken.value = true

                localStorage.setItem('accessToken', JSON.stringify(accessToken.value))
                localStorage.setItem('refreshToken', JSON.stringify(refreshToken.value))

                await getAuthorizedUser()
            }

            return data
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Google Login failed'
        } finally {
            isLoading.value = false
        }
        return null
    }

    const register = async (form: RegisterForm, url?: string) => {
        isLoading.value = true
        error.value = null

        try {
            const query = `mutation Register($input:RegistrationInput!) {
                register(input:$input) {status,message,data}
            }`;
            const variables = { input: form }
            const data = await window.$ajaxGraphql(query, variables, url || `/graphql/auth`)

            if (data.errors) {
                error.value = data.errors[0]?.message || 'Registration failed'
            } else if (data.register && data.register.status && data.register.data) {
                return data.register.data;
            } else {
                console.info("register", data);
            }
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Registration failed'
        } finally {
            isLoading.value = false
        }

        return null
    }

    const logout = async () => {
        var status = await requestLogout();

        if (status) {
            user.value = null;
            accessToken.value = null;
            refreshToken.value = null;

            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
        }
    }

    const getAuthorizedUser = async () => {
        var data = await requestCurrentUser();

        if (!data) {
            var status = await refreshAccessToken();

            if (status) {
                data = await requestCurrentUser();
            }
        }

        if (data) {
            user.value = data;
            accessToken.value = true;
            refreshToken.value = true;

            setUser(data);
            localStorage.setItem('accessToken', JSON.stringify(accessToken.value));
            localStorage.setItem('refreshToken', JSON.stringify(refreshToken.value));
        } else {
            logout();
        }
    }

    const requestCurrentUser = async (moduleName?: string, url?: string) => {
        var data = await fetch(`${url || '/auth'}/me${moduleName ? `/${moduleName}` : ``}`, {
            method: 'GET',
            credentials: 'include',  // Ensure cookies are sent with the request
            headers: {
                "Accept": "application/json",
                "Accept-Content": "application/json",
            }
        })
            .then(res => {
                var statusCode = res.status;
                if ([401, 402, 403].includes(statusCode)) {
                    return {
                        code: statusCode,
                        status: "SUCCESS",
                    }
                }

                return res.json()
            }).catch(err => console.error('Error:', err));

        if (data && data.id) {
            return data;
        } else if (data && [401, 402, 403].includes(data.code)) {
            return null;
        }

        return null;
    }

    const requestLogout = async (url?: string) => {
        var data = await fetch(`${url || '/auth/logout'}`, {
            method: 'GET',
            credentials: 'include',  // Ensure cookies are sent with the request
            headers: {
                "Accept": "application/json",
                "Accept-Content": "application/json",
            }
        })
            .then((res) => {
                var statusCode = res.status;
                if ([401, 402, 403].includes(statusCode)) {
                    return {
                        code: statusCode,
                        status: "SUCCESS",
                    }
                }

                return res.json()
            })
            .catch(err => console.error('Error:', err));

        if (data && data.status == "SUCCESS") {
            return true;
        }

        return false;
    }


    const requestMemberInfo = async () => {
        var res = await window.$ajaxGet("/member-info");

        console.log("requestMemberInfo", res)
        member.value = res;

        return res;
    }

    const refreshAccessToken = window.$refreshAccessToken

    const setUser = (data: User) => {
        console.info("Authorized user", data);
        user.value = data;
    }

    const setToken = (tokenValue: string) => {
        accessToken.value = tokenValue
        localStorage.setItem('accessToken', tokenValue)
    }

    const setRefreshToken = (tokenValue: string) => {
        refreshToken.value = tokenValue
        localStorage.setItem('refreshToken', tokenValue)
    }

    const restoreSession = () => {
        const savedToken = localStorage.getItem('accessToken')
        const savedRefreshToken = localStorage.getItem('refreshToken')

        if (savedToken && savedRefreshToken) {
            try {
                accessToken.value = JSON.parse(savedToken);
                refreshToken.value = JSON.parse(savedRefreshToken);

                // console.error('restoreSession', accessToken.value, refreshToken.value, isAuthenticated.value);
            } catch (e) {
                logout()
            }
        }
    }


    const redirectToAuth = (redirect?: string) => {
        if (window.location.pathname != import.meta.env.VITE_APP_BASE_URL) {
            var url = `${window.$baseUrl}${import.meta.env.DEV ? '/dev' : ''}/auth` + (redirect ? `?redirect=${redirect}` : ``);
            // console.log("redirectToAuth.url", url)

            window.location.replace(url)
        }
    }


    return {
        user,
        member,
        username,
        profileUrl,
        accessToken,
        refreshToken,
        isLoading,
        error,
        isAuthenticated,

        sendOTP,
        login,
        loginWithGoogle,
        logout,
        register,
        getAuthorizedUser,
        setUser,
        setToken,
        setRefreshToken,
        restoreSession,
        redirectToAuth,
        requestMemberInfo,
    }
})
